// Versión que muestra la aplicación: la del build (REACT_APP_VERSION, la calcula CI). En local no
// hay versión de build y se muestra «local».
// Para saber si hay una versión nueva, App.tsx compara la del build con /version.json, que CI
// publica en cada despliegue (issue #12, P5).

/** Etiqueta del ambiente según la versión: X.Y.Z-rc.N+sha es QA, X.Y.Z-dev.N+sha es desarrollo. */
export function versionEnvironment(version: string): "QA" | "DEV" | null {
  if (/-rc\./.test(version)) return "QA";
  if (/-dev\./.test(version)) return "DEV";
  return null;
}

export function displayVersion(): string {
  return process.env.REACT_APP_VERSION || "local";
}

/** Compara dos versiones SemVer (X.Y.Z[-pre][+build]); ignora +build, como SemVer. */
export function compareVersions(a: string, b: string): number {
  const parse = (v: string) => {
    const plain = v.split("+")[0];
    const dash = plain.indexOf("-");
    const core = dash < 0 ? plain : plain.slice(0, dash);
    const pre = dash < 0 ? "" : plain.slice(dash + 1);
    return { core: core.split(".").map(Number), pre: pre ? pre.split(".") : [] };
  };
  const x = parse(a);
  const y = parse(b);
  for (let i = 0; i < 3; i++) {
    const d = (x.core[i] || 0) - (y.core[i] || 0);
    if (d) return Math.sign(d);
  }
  // Una versión sin pre-release es mayor que cualquiera con pre-release.
  if (!x.pre.length || !y.pre.length) return Math.sign(y.pre.length - x.pre.length);
  for (let i = 0; i < Math.max(x.pre.length, y.pre.length); i++) {
    const p = x.pre[i];
    const q = y.pre[i];
    if (p === undefined) return -1;
    if (q === undefined) return 1;
    const pn = /^\d+$/.test(p);
    const qn = /^\d+$/.test(q);
    if (pn && qn) {
      const d = Number(p) - Number(q);
      if (d) return Math.sign(d);
    } else if (pn !== qn) {
      return pn ? -1 : 1;
    } else if (p !== q) {
      return p < q ? -1 : 1;
    }
  }
  return 0;
}

/**
 * La versión desplegada en este servidor, según /version.json. null si no se pudo leer: sin red,
 * o el servidor devolvió el index.html (rutas del SPA) en vez del JSON.
 */
export async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: "no-store" });
    if (!res.ok || !(res.headers.get("content-type") || "").includes("json")) return null;
    const body = await res.json();
    return typeof body?.version === "string" ? body.version : null;
  } catch {
    return null;
  }
}

/** true si el servidor tiene una versión mayor que la que corre en esta pestaña. */
export async function hasNewerDeployment(buildVersion: string): Promise<boolean> {
  const deployed = await fetchDeployedVersion();
  return !!deployed && compareVersions(deployed, buildVersion) > 0;
}
